import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { fg } from '@atlaskit/platform-feature-flags';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { TypeFilterValueKey } from '../../../../../common/constants/filters/universal-type';
import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as campaignStatusFilter } from '../../../../../controllers/store/search/filters/campaign-status';

import { i18n } from './messages';

const CAMPAIGN_STATUS_VISIBLE_TYPES = [TypeFilterValueKey.Campaign] as const;

const CampaignStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [typeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: campaignStatusFilter.id,
	});
	const isCampaignStatusFilterEnabled = fg('rovo-fps-3p-ux-sf-campaign-status-filter');
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!hasLoadedInitialOptions.current && isCampaignStatusFilterEnabled) {
			hasLoadedInitialOptions.current = true;
			loadInitialOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCampaignStatusFilterEnabled]);

	const selectedTypes = typeFilterData?.selectedOptions.map((option) => option.value) ?? [];
	const isAllTypeSelected = selectedTypes.length === 0;
	const isCampaignTypeSelected =
		selectedTypes.length > 0 &&
		selectedTypes.every((type) =>
			(CAMPAIGN_STATUS_VISIBLE_TYPES as readonly string[]).includes(type),
		);
	const shouldShowCampaignStatusFilter = isAllTypeSelected || isCampaignTypeSelected;

	useEffect(() => {
		if (isCampaignStatusFilterEnabled && !shouldShowCampaignStatusFilter) {
			clearOptions();
		}
	}, [clearOptions, isCampaignStatusFilterEnabled, shouldShowCampaignStatusFilter]);

	if (!filterData || !isCampaignStatusFilterEnabled || !shouldShowCampaignStatusFilter) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={campaignStatusFilter.id}
			label={formatMessage(i18n.campaignStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.campaignStatusFilterNoOptions)}
			iconBefore={<StatusInformationIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default CampaignStatusFilter;
